/* eslint-disable react-hooks/exhaustive-deps */
import { ClientsResponse } from '@efex-services/client/types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type DialogProps,
} from '@efexteam/components';
import { Typography } from '@mui/material';
import { ContactResult } from '@pages/clients/types';
import { selectProfileData } from '@pages/profile/store/selectors';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import DataLoadingWrapper from '../../components/common/DataLoadingWrapper';
import ErrorRetry from '../../components/common/ErrorRetry';
import { fetchClients } from '../../pages/clients/store';
import clientSvc from '../../services/client/clientSvc';
import { useAppDispatch } from '../../store';

type Client = ClientsResponse['results'][0];

interface DeleteClientModalProps extends DialogProps {
  onClose: () => void;
  client: Client;
}

const DeleteClientModal = ({
  open,
  onClose,
  client,
}: DeleteClientModalProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const userProfile = useSelector(selectProfileData);

  const [deleting, setDeleting] = useState(false);

  const {
    data,
    error,
    invoke: deleteContact,
  } = useApiCall((values) => clientSvc.deleteContact(values));

  const deleteSelected = () => {
    setDeleting(true);
    const { id: contactId } = client as unknown as ContactResult;
    const enterpriseId = userProfile?.company?.enterpriseId;
    deleteContact({ contactId, enterpriseId });
  };

  useEffect(() => {
    if (data) {
      onClose();
      dispatch(fetchClients({}));
    }
  }, [data, dispatch, onClose]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle showCloseButton onClick={onClose}>
        {t('dcTitle')}
      </DialogTitle>
      <DialogContent>
        <input type="hidden" name="client_id" defaultValue={client?.id} />
        <div className="form-group text-center">
          <Typography>{t('dcSubtitle')}</Typography>
          <Typography>
            <Trans
              i18nKey="dcConfirm"
              values={{
                contactName:
                  client?.contactNaturalPersonEnterprise?.naturalPerson
                    ?.firstName,
              }}
              components={{ strong: <strong /> }}
            />
          </Typography>
        </div>
      </DialogContent>
      <DialogActions
        className="flex !justify-around mt-4"
        style={{ gap: '20px' }}
      >
        {!error ? (
          <DataLoadingWrapper loadingData={deleting} variant="spinner">
            <Button
              appearance="primary"
              size="large"
              className="mt-4 w-full max-w-[150px]"
              onClick={deleteSelected}
            >
              {t('agree')}
            </Button>
          </DataLoadingWrapper>
        ) : (
          <ErrorRetry
            showImage={false}
            buttonClassNames="min-w-[150px]"
            onRetry={deleteSelected}
          />
        )}
        <Button
          appearance="secondary"
          size="large"
          className="mt-4 w-full max-w-[150px]"
          onClick={onClose}
        >
          {t('disagree')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteClientModal;
